import { Framebuffer } from './Framebuffer.js';
import { PostProcessManager } from './PostProcessManager.js';
import { EngineCamera } from './camera/EngineCamera.js';
import { Observable } from '../Observable.js';
import { Ray as BABYLON_Ray } from '@inter-ikea-betrakta-babylonjs/core/Culling/ray.js';
import { Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { matrix_pool, vector4_pool } from '../Math.js';
import { RENDER_LAYER_MASK_DEFAULT, RenderLayerMask } from './RenderLayer.js';
import { RenderingManager as BABYLON_RenderingManager } from '@inter-ikea-betrakta-babylonjs/core/Rendering/renderingManager.js';
import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
export class RenderView {
    constructor(viewer, interaction_system, render_width, render_height, framebuffer_type, wanted_msaa_samples) {
        this._is_dirty = false;
        // We keep our own render size which is what the user sets.
        // This lets us resize the actual framebuffer only when needed (just before we render)
        this._render_size = { x: 0, y: 0 };
        this._outlines_enabled = true;
        this._enabled = true;
        this.auto_capture_input_focus = true;
        this.viewport_changed_observable = new Observable();
        this.render_size_changed_observable = new Observable();
        this.before_render_observable = new Observable();
        this.after_render_observable = new Observable();
        this.camera_updated_observable = new Observable();
        this._render_layer_mask = RENDER_LAYER_MASK_DEFAULT;
        // Internal arrays for rendering
        this._opaque_render_list = [];
        this._transparent_and_unlit_render_list = [];
        this._draw_on_top_render_list = [];
        this.custom_clear_color = null;
        render_width = Math.floor(render_width);
        render_height = Math.floor(render_height);
        this._render_size.x = render_width;
        this._render_size.y = render_height;
        this.viewer = viewer;
        this.engine_camera = new EngineCamera(viewer);
        this.engine_camera.camera.layerMask = this._render_layer_mask.bitmap;
        const enable_depth_renderer = Framebuffer.is_depth_renderer_supported(this.viewer.scene.getEngine());
        const msaa_samples = Framebuffer.get_supported_msaa_samples(this.viewer.scene.getEngine(), wanted_msaa_samples, framebuffer_type);
        this.framebuffer = new Framebuffer(this.viewer.scene, framebuffer_type, render_width, render_height, this.engine_camera.camera, msaa_samples, enable_depth_renderer);
        this.framebuffer.get_render_target().renderList = [];
        this.postprocess_manager = new PostProcessManager(this.viewer, this.framebuffer, viewer.ao_supported(), this.viewer.scene, this.engine_camera, () => { this.set_view_dirty(); });
        for (let index = BABYLON_RenderingManager.MIN_RENDERINGGROUPS; index < BABYLON_RenderingManager.MAX_RENDERINGGROUPS; index++) {
            this.framebuffer.get_render_target().setRenderingAutoClearDepthStencil(index, false);
        }
        this.framebuffer.get_render_target().skipInitialClear = true;
    }
    get_aspect_ratio() {
        return this._render_size.x / this._render_size.y;
    }
    update(_) {
        if (!this._enabled)
            return;
        if (this.engine_camera.is_dirty()) {
            this.set_view_dirty();
            this.engine_camera.clear_dirty();
            this.camera_updated_observable.notify(this, this.engine_camera);
        }
    }
    set_view_dirty() {
        this._is_dirty = true;
        this.postprocess_manager.reset_stable_frames();
    }
    /**
     * Renders the View into its framebuffer, with all its postprocessing effects.
     * @param custom_render_list If set to null, the View renders any meshes that matches the View's layer mask.
     * However, if defined, those meshes will be rendered regardless of layer mask (needed for screenshots).
     */
    render(custom_render_list = null) {
        var _a;
        if (!this._enabled)
            return;
        if (this.is_framebuffer_size_dirty()) {
            this.framebuffer.resize(this._render_size.x, this._render_size.y);
            this.postprocess_manager.resize();
            this.render_size_changed_observable.notify(this, { x: this._render_size.x, y: this._render_size.y });
        }
        this.before_render_observable.notify(this);
        const previous_active_camera = this.viewer.scene._activeCamera;
        this.framebuffer.bind();
        // Clear color + depth
        const engine = this.viewer.scene.getEngine();
        engine.setColorWrite(true);
        engine.setDepthWrite(true);
        engine.setStencilMask(0b11111111);
        engine.applyStates();
        engine.clear((_a = this.custom_clear_color) !== null && _a !== void 0 ? _a : this.viewer.scene.clearColor, true, true, true);
        // Increment scene render id for good measure (sometimes Babylon doesn't render otherwise)
        this.viewer.scene.incrementRenderId();
        // Render forward pass (render geometry)
        const scene = this.viewer.scene;
        scene._activeCamera = this.engine_camera.camera;
        this.framebuffer.bind();
        // Jitter for TAA if applicable
        this.postprocess_manager.jitter(this._render_size.x, this._render_size.y);
        scene.resetCachedMaterial();
        scene.setTransformMatrix(this.engine_camera.view_matrix, this.engine_camera.jittered_projection_matrix);
        this._opaque_render_list.length = 0;
        this._transparent_and_unlit_render_list.length = 0;
        this._draw_on_top_render_list.length = 0;
        const all_meshes = custom_render_list === null ? this.viewer.get_meshes_by_layer_mask(this._render_layer_mask) : custom_render_list;
        for (let i = 0; i < all_meshes.length; i++) {
            const mesh = all_meshes[i];
            const material = mesh.material;
            // Perform frustum culling
            if (!this.engine_camera.camera.isInFrustum(mesh) || material === null)
                continue;
            if (material && material.depthFunction === BABYLON_Engine.ALWAYS) {
                this._draw_on_top_render_list.push(mesh);
            }
            else if ((material && material.unlit)) {
                this._transparent_and_unlit_render_list.push(mesh);
            }
            else {
                if ((material === null || material === void 0 ? void 0 : material.transparencyMode) === 2 || (material === null || material === void 0 ? void 0 : material.transparencyMode) === 3) {
                    this._transparent_and_unlit_render_list.push(mesh);
                }
                else {
                    this._opaque_render_list.push(mesh);
                }
            }
        }
        const render_target = this.framebuffer.get_render_target();
        render_target.activeCamera = this.engine_camera.camera;
        render_target.render_lite(this.engine_camera, this._opaque_render_list);
        // Render AO
        this.framebuffer.prepare_depth_buffer_texture();
        this.postprocess_manager.render_ambient_occlusion();
        // Re-bind framebuffer after AO has rendered and render the rest of the objects
        this.framebuffer.bind();
        render_target.render_lite(this.engine_camera, this._transparent_and_unlit_render_list);
        render_target.render_lite(this.engine_camera, this._draw_on_top_render_list);
        this.viewer.engine.setStencilOperationPass(this.viewer.engine._gl.KEEP);
        this.viewer.engine.applyStates();
        // Perform postprocessing (bloom, accumulation etc)
        this.postprocess_manager.render(render_target.renderTarget, this.framebuffer.get_depth_texture());
        this.viewer.scene._activeCamera = previous_active_camera;
        this.postprocess_manager.increase_stable_frames();
        if (!this.postprocess_manager.keep_rendering()) {
            this._is_dirty = false;
        }
        this.after_render_observable.notify(this);
    }
    get_render_layer_mask() {
        return this._render_layer_mask;
    }
    set_render_layer_mask(mask) {
        if (this._render_layer_mask.bitmap === mask.bitmap)
            return;
        this._render_layer_mask = RenderLayerMask.clone(mask);
        this.engine_camera.camera.layerMask = this._render_layer_mask.bitmap;
        this.set_view_dirty();
    }
    get_render_width() {
        return this._render_size.x;
    }
    get_render_height() {
        return this._render_size.y;
    }
    get_render_size() {
        return {
            x: this._render_size.x,
            y: this._render_size.y
        };
    }
    set_render_size(width_size, height) {
        if (typeof width_size === 'number') {
            this.resize(width_size, height);
        }
        else {
            this.resize(width_size.x, width_size.y);
        }
    }
    is_dirty() {
        return this._is_dirty;
    }
    get outlines_enabled() {
        return this._outlines_enabled;
    }
    set outlines_enabled(enabled) {
        if (this._outlines_enabled === enabled)
            return;
        this._outlines_enabled = enabled;
        this.viewer.mark_composite_dirty();
    }
    get enabled() {
        return this._enabled;
    }
    set enabled(enabled) {
        if (this._enabled && !enabled) {
            // We're disabling
            this.viewer.mark_composite_dirty();
        }
        else if (!this.enabled && enabled) {
            // We're enabling
            this.set_view_dirty();
        }
        this._enabled = enabled;
    }
    dispose() {
        const was_enabled = this.enabled;
        this._enabled = false;
        this.postprocess_manager.dispose();
        this.engine_camera.dispose();
        this.framebuffer.dispose();
        this.viewport_changed_observable.dispose();
        this.render_size_changed_observable.dispose();
        this.before_render_observable.dispose();
        this.after_render_observable.dispose();
        this.camera_updated_observable.dispose();
        if (was_enabled)
            this.viewer.mark_composite_dirty();
    }
    create_ray_from_normalized_coordinates(normalized_x, normalized_y) {
        const ray = BABYLON_Ray.Zero();
        this.create_ray_to_ref(normalized_x, normalized_y, ray);
        return { origin: ray.origin, direction: ray.direction, length: ray.length };
    }
    /**
     * Creates a ray originating from the image plane, towards the respective point on the far plane.
     * Useful for picking
     * @param normalized_x The normalized x coordinate in the viewport (range 0-1)
     * @param normalized_y The normalized y coordinate in the viewport (range 0-1)
     * @param ref The ray object to write into.
     */
    create_ray_to_ref(normalized_x, normalized_y, ref) {
        // Fetch the View's current view-projection matrix and invert it
        const inverse_view_projection_matrix = this.engine_camera.view_matrix.multiplyToRef(this.engine_camera.projection_matrix, matrix_pool.fetch());
        inverse_view_projection_matrix.invert();
        const ndc_x = normalized_x * 2.0 - 1.0;
        const ndc_y = -(normalized_y * 2.0 - 1.0);
        // Project the given normalized coordinates onto the image (near) and far plane.
        const near_projected = BABYLON_Vector4.TransformCoordinatesFromFloatsToRef(ndc_x, ndc_y, -1, inverse_view_projection_matrix, vector4_pool.fetch());
        const far_projected = BABYLON_Vector4.TransformCoordinatesFromFloatsToRef(ndc_x, ndc_y, 1, inverse_view_projection_matrix, vector4_pool.fetch());
        near_projected.scaleInPlace(1.0 / near_projected.w);
        far_projected.scaleInPlace(1.0 / far_projected.w);
        // Set the origin to the position on the near/image plane
        ref.origin.x = near_projected.x;
        ref.origin.y = near_projected.y;
        ref.origin.z = near_projected.z;
        // Calculate the direction as the vector from the point on the near plane to the point on the far plane
        ref.direction.x = far_projected.x - near_projected.x;
        ref.direction.y = far_projected.y - near_projected.y;
        ref.direction.z = far_projected.z - near_projected.z;
        const ray_length = ref.direction.length();
        ref.direction.normalizeFromLength(ray_length);
        vector4_pool.return(near_projected, far_projected);
        matrix_pool.return(inverse_view_projection_matrix);
    }
    /**
     * Resizes the render target for the view, updating its rendering resolution
     */
    resize(render_width, render_height) {
        render_width = Math.floor(render_width);
        render_height = Math.floor(render_height);
        if (this._render_size.x === render_width && this._render_size.y === render_height)
            return;
        this._render_size.x = render_width;
        this._render_size.y = render_height;
        this.set_view_dirty();
    }
    // Returns true if the framebuffer needs to update its size according to the set render size
    is_framebuffer_size_dirty() {
        return this.framebuffer.get_width() !== this._render_size.x || this.framebuffer.get_height() !== this._render_size.y;
    }
    project_world_position_to_ndc(world_position) {
        const view_projection_matrix = this.engine_camera.view_matrix.multiplyToRef(this.engine_camera.projection_matrix, matrix_pool.fetch());
        const projected = BABYLON_Vector4.TransformCoordinatesFromFloatsToRef(world_position.x, world_position.y, world_position.z, view_projection_matrix, vector4_pool.fetch());
        projected.scaleInPlace(1.0 / projected.w);
        const ndc = {
            x: projected.x,
            y: projected.y,
            z: projected.z
        };
        vector4_pool.return(projected);
        matrix_pool.return(view_projection_matrix);
        return ndc;
    }
    // EXPERIMENTAL
    set_experimental_edge_sketch_effect_enabled(enabled) {
        if (enabled) {
            this.postprocess_manager.enable_edge_sketch_effect();
        }
        else {
            this.postprocess_manager.disable_edge_sketch_effect();
        }
    }
}
//# sourceMappingURL=RenderView.js.map